import React from "react";
import { Icon } from "@iconify/react";
import screenRecherche from "../images/app-screens/recherche.jpg";
import screenChat from "../images/app-screens/chat-offre.jpg";
import screenFavoris from "../images/app-screens/favoris.jpg";

/**
 * Animated hero illustration: two floating phones running the OmniCave app.
 *
 *  - Front phone: the « Découvrir » screen. Region chips scroll sideways, the
 *    wine listing scrolls up endlessly, then a tap opens a wine in a bottom
 *    sheet (price, verified seller, « Acheter » / « Faire une offre »).
 *  - Back phone: a buyer/seller chat where an offer is negotiated and
 *    accepted, the payment going into escrow.
 *  - Floating cards around them repeat the three promises of the product.
 *
 * Everything is CSS-animated (see sass/Hero.scss); the whole scene is drawn
 * at a fixed size and scaled with the `--hero-scale` variable. Purely
 * decorative: hidden from assistive technology.
 */

const REGIONS = ["Tous", "Bordeaux", "Bourgogne", "Champagne", "Rhône", "Loire", "Alsace", "Provence"];

const WINES = [
  { name: "Château Chasse-Spleen", year: 2018, region: "Moulis-en-Médoc", price: "30 €", tone: "bordeaux" },
  { name: "Léoville Poyferré", year: 2005, region: "Saint-Julien", price: "150 €", tone: "bordeaux", badge: "Coup de cœur" },
  { name: "Meursault 1er Cru", year: 2019, region: "Bourgogne", price: "95 €", tone: "white" },
  { name: "Pavillon Rouge", year: 2003, region: "Margaux", price: "180 €", tone: "bordeaux" },
  { name: "Blanc de Blancs", year: 2015, region: "Champagne", price: "68 €", tone: "champagne", badge: "Rare" },
  { name: "Châteauneuf-du-Pape", year: 2016, region: "Rhône", price: "55 €", tone: "rhone" },
  { name: "Sancerre Rosé", year: 2022, region: "Loire", price: "22 €", tone: "rose" },
  { name: "Pontet-Canet", year: 2010, region: "Pauillac", price: "210 €", tone: "bordeaux" },
];

const TONES = {
  bordeaux: { glass: "#24352b", cap: "#7a1230", label: "#f6efe2", line: "#a51d43" },
  rhone: { glass: "#2f3a2a", cap: "#15151a", label: "#f3ead6", line: "#3b3b3b" },
  white: { glass: "#cfd9a4", cap: "#c9a24a", label: "#fffaf0", line: "#8a7a3a" },
  champagne: { glass: "#3a4a2c", cap: "#d4af37", label: "#ffffff", line: "#b08d2a" },
  rose: { glass: "#eab0b8", cap: "#e07a90", label: "#fff6f7", line: "#c2506a" },
};

/** A stylised wine bottle; `tone` picks the glass / capsule / label colours. */
export function Bottle({ tone = "bordeaux", className = "" }) {
  const t = TONES[tone] || TONES.bordeaux;
  return (
    <svg viewBox="0 0 40 120" className={`bottle ${className}`.trim()} aria-hidden="true" focusable="false">
      <path
        d="M15 2h10v22c0 4 9 7 9 16v70a8 8 0 0 1-8 8H14a8 8 0 0 1-8-8V40c0-9 9-12 9-16z"
        fill={t.glass}
      />
      <path d="M11 44c0-5 4-8 4-12V8h3v24c0 5-4 7-4 12v64h-3z" fill="rgba(255,255,255,0.2)" />
      <rect x="14" y="1" width="12" height="16" rx="2" fill={t.cap} />
      <rect x="14" y="13" width="12" height="1.5" fill="rgba(0,0,0,0.25)" />
      <rect x="9" y="58" width="22" height="30" rx="2" fill={t.label} />
      <rect x="13" y="64" width="14" height="2.2" rx="1" fill={t.line} />
      <rect x="15" y="69.5" width="10" height="2" rx="1" fill={t.line} opacity="0.6" />
      <rect x="12" y="78" width="16" height="1.5" rx="1" fill={t.line} opacity="0.4" />
      <rect x="12" y="82" width="16" height="1.5" rx="1" fill={t.line} opacity="0.3" />
    </svg>
  );
}

function WineCard({ wine }) {
  return (
    <div className="wineCard">
      <div className="wineCard__img">
        <Bottle tone={wine.tone} />
        {wine.badge && <span className="wineCard__badge">{wine.badge}</span>}
        <span className="wineCard__fav">
          <Icon icon="mdi:heart-outline" />
        </span>
      </div>
      <strong>{wine.name}</strong>
      <span>
        {wine.year} · {wine.region}
      </span>
      <em>{wine.price}</em>
    </div>
  );
}

function FeedHalf({ title, hint }) {
  return (
    <div className="app__half">
      <div className="app__label">
        {title}
        <small>{hint}</small>
      </div>
      <div className="app__grid">
        {WINES.map((w) => (
          <WineCard key={w.name} wine={w} />
        ))}
      </div>
    </div>
  );
}

function BrowsePhone() {
  return (
    <div className="phone phone--front">
      <div className="phone__screen">
        <div className="app">
          <div className="app__top">
            <div className="app__status">
              <span>9:41</span>
              <span className="app__statusIcons">
                <i />
                <i />
                <i />
                <i />
              </span>
            </div>
            <div className="app__brand">
              <div>
                <small>Bonjour Claire 👋</small>
                <h5>Découvrir les caves</h5>
              </div>
              <span className="app__bell">
                <Icon icon="mdi:bell-outline" />
              </span>
            </div>
            <div className="app__search">
              <Icon icon="mdi:magnify" />
              <span>Rechercher un vin, un domaine, un millésime…</span>
            </div>
          </div>

          <div className="app__chips">
            <div className="app__chipsTrack">
              {[...REGIONS, ...REGIONS].map((r, i) => (
                <span key={i} className={r === "Tous" ? "is-on" : undefined}>
                  {r}
                </span>
              ))}
            </div>
          </div>

          <div className="app__feed">
            <div className="app__list">
              <FeedHalf title="Nouveautés" hint="Voir tout" />
              <FeedHalf title="Les plus consultés" hint="Voir tout" />
            </div>
            <span className="app__tap" />
          </div>

          <div className="app__nav">
            <span className="is-on">
              <Icon icon="mdi:home-variant" />
            </span>
            <span>
              <Icon icon="mdi:compass-outline" />
            </span>
            <span className="app__navPlus">
              <Icon icon="mdi:plus" />
            </span>
            <span>
              <Icon icon="mdi:message-outline" />
            </span>
            <span>
              <Icon icon="mdi:account-outline" />
            </span>
          </div>

          <div className="app__dim" />
          <div className="app__sheet">
            <div className="sheet__hero">
              <Bottle tone="bordeaux" />
            </div>
            <div className="sheet__head">
              <div>
                <strong>Château Léoville Poyferré</strong>
                <span>2005 · Saint-Julien · 75 cl</span>
              </div>
              <em>150 €</em>
            </div>
            <div className="sheet__meta">
              <span>
                <Icon icon="mdi:check-decagram" /> Vendeur vérifié
              </span>
              <span>
                <Icon icon="mdi:shield-lock-outline" /> Séquestre
              </span>
              <span>
                <Icon icon="mdi:truck-outline" /> Sendcloud
              </span>
            </div>
            <div className="sheet__btns">
              <span className="is-primary">Acheter maintenant</span>
              <span>Faire une offre</span>
            </div>
          </div>
        </div>
      </div>
      <span className="phone__notch" />
    </div>
  );
}

function ChatPhone() {
  return (
    <div className="phone phone--back">
      <div className="phone__screen">
        <div className="chat">
          <div className="chat__top">
            <Icon icon="mdi:arrow-left" />
            <span className="chat__avatar">M</span>
            <div>
              <strong>Marc L.</strong>
              <small>Vendeur vérifié · en ligne</small>
            </div>
          </div>
          <div className="chat__product">
            <Bottle tone="bordeaux" />
            <div>
              <strong>Pavillon Rouge 2003</strong>
              <span>Prix affiché</span>
            </div>
            <em>180 €</em>
          </div>
          <div className="chat__body">
            <div className="chat__bubble chat__bubble--me chat__step-1">Bonjour ! Je vous propose 160 € pour la bouteille ?</div>
            <div className="chat__typing">
              <i />
              <i />
              <i />
            </div>
            <div className="chat__bubble chat__bubble--them chat__step-2">Je peux descendre à 170 €, expédition incluse.</div>
            <div className="chat__bubble chat__bubble--me chat__step-3">Parfait, j’accepte 👍</div>
            <div className="chat__event chat__step-4">
              <Icon icon="mdi:check-circle" />
              Offre acceptée · 170 €
              <small>Paiement placé sous séquestre</small>
            </div>
          </div>
          <div className="chat__input">
            <span>Votre message…</span>
            <i>
              <Icon icon="mdi:send" />
            </i>
          </div>
        </div>
      </div>
      <span className="phone__notch" />
    </div>
  );
}

export default function HeroMockup() {
  return (
    <div className="heroScene" aria-hidden="true">
      <div className="heroScene__inner">
        <div className="heroScene__glow" />
        <div className="heroRing heroRing--1">
          <i />
          <i />
          <i />
        </div>
        <div className="heroRing heroRing--2">
          <i />
          <i />
        </div>

        <div className="heroScene__item heroScene__item--back hero-pop" style={{ "--d": "0.25s" }}>
          <ChatPhone />
        </div>
        <div className="heroScene__item heroScene__item--front hero-pop" style={{ "--d": "0.45s" }}>
          <BrowsePhone />
        </div>

        <div className="heroScene__item heroScene__item--chip1 hero-pop" style={{ "--d": "0.9s" }}>
          <div className="heroChip">
            <span className="heroChip__icon">
              <Icon icon="mdi:shield-lock-outline" />
            </span>
            <div>
              <strong>Paiement sécurisé</strong>
              <small>Compte séquestre</small>
            </div>
          </div>
        </div>
        <div className="heroScene__item heroScene__item--chip2 hero-pop" style={{ "--d": "1.1s" }}>
          <div className="heroChip">
            <span className="heroChip__icon">
              <Icon icon="mdi:truck-fast-outline" />
            </span>
            <div>
              <strong>Suivi en temps réel</strong>
              <small>Expédition Sendcloud</small>
            </div>
          </div>
        </div>
        <div className="heroScene__item heroScene__item--chip3 hero-pop" style={{ "--d": "1.3s" }}>
          <div className="heroChip heroChip--green">
            <span className="heroChip__icon">
              <Icon icon="mdi:percent-outline" />
            </span>
            <div>
              <strong>0 € de frais à vie</strong>
              <small>Membres fondateurs</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* « Application mobile » section: checkout + delivery tracking              */
/* ------------------------------------------------------------------------ */

const CART = [
  { name: "Château Chasse-Spleen", year: 2018, region: "Moulis", price: "30 €", tone: "bordeaux" },
  { name: "Léoville Poyferré", year: 2005, region: "Saint-Julien", price: "150 €", tone: "bordeaux" },
  { name: "Pavillon Rouge", year: 2003, region: "Margaux", price: "180 €", tone: "bordeaux" },
];

function CheckoutPhone() {
  return (
    <div className="phone phone--front phone--mirror">
      <div className="phone__screen">
        <div className="pay">
          <div className="pay__top">
            <Icon icon="mdi:arrow-left" />
            <strong>Mon panier</strong>
            <span>3</span>
          </div>
          <div className="pay__items">
            {CART.map((c) => (
              <div className="pay__item" key={c.name}>
                <Bottle tone={c.tone} />
                <div>
                  <strong>{c.name}</strong>
                  <span>
                    {c.year} · {c.region}
                  </span>
                </div>
                <em>{c.price}</em>
              </div>
            ))}
          </div>
          <div className="pay__receipt">
            <h6>Reçu</h6>
            <div>
              <span>Total</span>
              <b>360 €</b>
            </div>
            <div>
              <span>Livraison · Sendcloud</span>
              <b>10 €</b>
            </div>
            <div className="is-total">
              <span>Payer ma commande</span>
              <b>370 €</b>
            </div>
          </div>
          <div className="pay__safe">
            <Icon icon="mdi:shield-lock-outline" />
            Fonds bloqués sous séquestre jusqu’à la réception
          </div>
          <div className="pay__btn">
            Payer 370 €
            <span className="pay__tap" />
          </div>

          <div className="pay__done">
            <span className="pay__check">
              <svg viewBox="0 0 52 52" aria-hidden="true">
                <circle cx="26" cy="26" r="24" />
                <path d="M15 27l8 8 15-16" />
              </svg>
            </span>
            <strong>Paiement sécurisé !</strong>
            <span>
              370 € placés sous séquestre.
              <br />
              Le vendeur prépare votre colis.
            </span>
            <em>Suivre ma commande</em>
          </div>
        </div>
      </div>
      <span className="phone__notch" />
    </div>
  );
}

function TrackingPhone() {
  return (
    <div className="phone phone--back phone--mirror">
      <div className="phone__screen">
        <div className="track">
          <div className="track__top">
            <Icon icon="mdi:arrow-left" />
            <div>
              <strong>Suivi de commande</strong>
              <small>N° 4821 · 3 bouteilles</small>
            </div>
          </div>
          <div className="track__order">
            <span className="track__bottles">
              <Bottle tone="bordeaux" />
              <Bottle tone="white" />
              <Bottle tone="bordeaux" />
            </span>
            <div>
              <strong>Expédié par Marc L.</strong>
              <span>Sendcloud · Colissimo</span>
            </div>
          </div>
          <ol className="track__steps">
            <li className="track__step-1">
              <i>
                <Icon icon="mdi:check-bold" />
              </i>
              <div>
                <strong>Paiement sécurisé</strong>
                <small>Fonds placés sous séquestre</small>
              </div>
            </li>
            <li className="track__step-2">
              <i>
                <Icon icon="mdi:package-variant-closed" />
              </i>
              <div>
                <strong>Colis expédié</strong>
                <small>Étiquette Sendcloud générée</small>
              </div>
            </li>
            <li className="track__step-3">
              <i>
                <Icon icon="mdi:truck-fast-outline" />
              </i>
              <div>
                <strong>En cours de livraison</strong>
                <small>Arrivée prévue demain</small>
              </div>
            </li>
            <li className="track__step-4 is-last">
              <i>
                <Icon icon="mdi:home-variant-outline" />
              </i>
              <div>
                <strong>Livré · fonds versés</strong>
                <small>Le vendeur est payé</small>
              </div>
            </li>
          </ol>
          <div className="track__cta">
            <Icon icon="mdi:message-outline" /> Contacter le vendeur
          </div>
        </div>
      </div>
      <span className="phone__notch" />
    </div>
  );
}

/**
 * Second animated scene, for the « Application mobile » section: paying for
 * a cart (escrow) on the front phone, following the parcel on the back one.
 * Mirrored layout; entrance animations start when it scrolls into view.
 */
export function DownloadMockup() {
  const box = React.useRef(null);
  const [seen, setSeen] = React.useState(false);

  React.useEffect(() => {
    if (!box.current || seen) return undefined;
    if (!("IntersectionObserver" in window)) {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(box.current);
    return () => observer.disconnect();
  }, [seen]);

  return (
    <div
      className={`heroScene heroScene--mirror heroScene--lazy${seen ? " is-seen" : ""}`}
      aria-hidden="true"
      ref={box}
    >
      <div className="heroScene__inner">
        <div className="heroScene__glow" />
        <div className="heroRing heroRing--1">
          <i />
          <i />
          <i />
        </div>
        <div className="heroRing heroRing--2">
          <i />
          <i />
        </div>

        <div className="heroScene__item heroScene__item--back hero-pop" style={{ "--d": "0.1s" }}>
          <TrackingPhone />
        </div>
        <div className="heroScene__item heroScene__item--front hero-pop" style={{ "--d": "0.3s" }}>
          <CheckoutPhone />
        </div>

        <div className="heroScene__item heroScene__item--chip1 hero-pop" style={{ "--d": "0.7s" }}>
          <div className="heroChip">
            <span className="heroChip__icon">
              <Icon icon="mdi:truck-fast-outline" />
            </span>
            <div>
              <strong>Colis suivi en direct</strong>
              <small>Expédition Sendcloud</small>
            </div>
          </div>
        </div>
        <div className="heroScene__item heroScene__item--chip2 hero-pop" style={{ "--d": "0.9s" }}>
          <div className="heroChip heroChip--green">
            <span className="heroChip__icon">
              <Icon icon="mdi:lock-open-check-outline" />
            </span>
            <div>
              <strong>Fonds libérés</strong>
              <small>À la réception du colis</small>
            </div>
          </div>
        </div>
        <div className="heroScene__item heroScene__item--chip3 hero-pop" style={{ "--d": "1.1s" }}>
          <div className="heroChip heroChip--gold">
            <span className="heroChip__icon">
              <Icon icon="mdi:star" />
            </span>
            <div>
              <strong>4,9 / 5</strong>
              <small>Vendeurs vérifiés</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* « À propos » section: one phone cycling through real app screens          */
/* ------------------------------------------------------------------------ */


// Inner screen size of the phone drawn in Hero.scss (.heroScene--about).
const ABOUT_SCREEN_W = 252;
const ABOUT_SCREEN_H = 542;

// Screens exported from the Figma mock-ups (OmniCave-Mockups, nodes
// 2235:2835, 2235:1610, 2235:3677). `ratio` = image height / width, used to
// pan tall screens slowly while they are shown.
const APP_SCREENS = [
  { src: screenRecherche, ratio: 2092 / 750, label: "Recherche & filtres", icon: "mdi:magnify" },
  { src: screenChat, ratio: 1920 / 750, label: "Offre négociée par chat", icon: "mdi:message-text-outline" },
  { src: screenFavoris, ratio: 1624 / 750, label: "Mes favoris", icon: "mdi:heart-outline" },
];

export function AboutMockup() {
  const box = React.useRef(null);
  const [seen, setSeen] = React.useState(false);

  React.useEffect(() => {
    if (!box.current || seen) return undefined;
    if (!("IntersectionObserver" in window)) {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(box.current);
    return () => observer.disconnect();
  }, [seen]);

  return (
    <div
      className={`heroScene heroScene--about heroScene--lazy${seen ? " is-seen" : ""}`}
      aria-hidden="true"
      ref={box}
    >
      <div className="heroScene__inner">
        <div className="heroScene__glow" />
        <div className="heroRing heroRing--1">
          <i />
          <i />
          <i />
        </div>
        <div className="heroRing heroRing--2">
          <i />
          <i />
        </div>

        <div className="heroScene__item heroScene__item--solo hero-pop" style={{ "--d": "0.1s" }}>
          <div className="phone phone--solo">
            <div className="phone__screen">
              <div className="slides">
                {APP_SCREENS.map((s, i) => (
                  <img
                    key={s.label}
                    src={s.src}
                    alt=""
                    className={`slides__img slides__img-${i + 1}`}
                    style={{
                      "--pan": `${Math.max(0, Math.round(s.ratio * ABOUT_SCREEN_W - ABOUT_SCREEN_H))}px`,
                    }}
                  />
                ))}
              </div>
            </div>
            <span className="phone__notch" />
          </div>
        </div>

        <div className="heroScene__item heroScene__item--tag hero-pop" style={{ "--d": "0.5s" }}>
          <div className="slideTag">
            {APP_SCREENS.map((s, i) => (
              <span key={s.label} className={`slideTag__item slideTag__item-${i + 1}`}>
                <i>
                  <Icon icon={s.icon} />
                </i>
                {s.label}
              </span>
            ))}
          </div>
        </div>

        <div className="heroScene__item heroScene__item--dots hero-pop" style={{ "--d": "0.7s" }}>
          <div className="slideDots">
            {APP_SCREENS.map((s, i) => (
              <i key={s.label}>
                <b className={`slideTag__item-${i + 1}`} />
              </i>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
